import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGetCurrentUser } from '@wecasa/auth/src/authentication-context';

import type { CommonModalProps } from '@wecasa/common/src/types/components';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { isCleaningReference, isWellnessReference } from '@wecasa/common/src/utils/universes';

import { getBasketMode, isSpotCleaningSelector, setBasketAttributeValue } from '@wecasa/customer/src/ducks/basket';
import { getUniverseRef } from '@wecasa/customer/src/ducks/basket-selectors';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@wecasa/membership/src/constants/country-config';
import useHasOrWantsMembership from '@wecasa/membership/src/hooks/use-has-or-wants-membership';
import type { MembershipOptIn } from '@wecasa/membership/src/types/membership';

import { useAppSelector } from '../../store';
import { trackMembershipEvent } from '../tracking';
import CleaningMembershipPaywall from './cleaning/cleaning-membership-paywall';
import WellnessMembershipPaywall from './wellness/wellness-membership-paywall';

export type MembershipPaywallProps = CommonModalProps & {
  isClosable?: boolean;
  hasNoInitialOptInChoiceSelection?: boolean;
};

const MembershipPaywall = ({
  isOpen,
  onClose,
  isClosable = true,
  hasNoInitialOptInChoiceSelection,
}: MembershipPaywallProps) => {
  const dispatch = useDispatch();

  const currentCountry = getCurrentCountry();

  const universeReference = useAppSelector(getUniverseRef);
  const user = useGetCurrentUser();
  const mode = useAppSelector(getBasketMode);
  const hasOrWantsMembership = useHasOrWantsMembership();
  const isSpotCleaning = useAppSelector(isSpotCleaningSelector);

  const [optInChoice, setOptInChoice] = useState<MembershipOptIn | undefined>(
    hasNoInitialOptInChoiceSelection ? undefined : 'noMembership',
  );

  const onOptInChoiceSelection = useCallback(
    (newOptIn: MembershipOptIn) => {
      trackMembershipEvent({
        eventType: newOptIn === 'membership' ? 'MemberPriceClick' : 'StandardPriceClick',
        user,
        universeReference,
        mode,
        path: 'MembershipPaywall',
      });

      setOptInChoice(newOptIn);
    },
    [user, universeReference, mode],
  );

  useEffect(() => {
    // To synchronise properly when paywall is opened again, but flag in redux changed
    // Also to track initial selection
    if (isOpen && (isWellnessReference(universeReference) || isSpotCleaning) && !hasNoInitialOptInChoiceSelection) {
      onOptInChoiceSelection(hasOrWantsMembership ? 'membership' : 'noMembership');
    }
  }, [
    isOpen,
    hasOrWantsMembership,
    onOptInChoiceSelection,
    universeReference,
    isSpotCleaning,
    hasNoInitialOptInChoiceSelection,
  ]);

  const closePaywall = useCallback(() => {
    trackMembershipEvent({
      eventType: 'ClosePaywallButtonClick',
      user,
      universeReference,
      mode,
      path: 'MembershipPaywall',
    });

    onClose();
  }, [onClose, universeReference, user, mode]);

  const onConfirmPaywall = useCallback(() => {
    trackMembershipEvent({
      eventType: 'ConfirmButtonClick',
      user,
      universeReference,
      mode,
      path: 'MembershipPaywall',
    });

    dispatch(setBasketAttributeValue('isMembershipRequested', optInChoice === 'membership'));
    onClose();
  }, [user, universeReference, dispatch, onClose, mode, optInChoice]);

  // Do not render membership paywall by accident for countries which do not support it
  // This is especially important for tests
  if (!MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry].isAvailable) return null;

  if (isWellnessReference(universeReference)) {
    return (
      <WellnessMembershipPaywall
        isOpen={isOpen}
        closePaywall={isClosable ? closePaywall : undefined}
        onConfirmPaywall={onConfirmPaywall}
        optInChoice={optInChoice}
        onOptInChoiceSelection={onOptInChoiceSelection}
        universeReference={universeReference}
      />
    );
  }

  if (isCleaningReference(universeReference)) {
    return (
      <CleaningMembershipPaywall
        isOpen={isOpen}
        closePaywall={isClosable ? closePaywall : undefined}
        onConfirmPaywall={onConfirmPaywall}
        optInChoice={optInChoice}
        onOptInChoiceSelection={onOptInChoiceSelection}
        user={user}
      />
    );
  }

  return null;
};

export default MembershipPaywall;
